import { useEffect } from "react";
import { Link } from "react-router-dom";
import { Toaster } from "sonner";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import "@/App.css";

import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { OfflineProvider } from "@/contexts/OfflineContext";
import { ScriptSettingsProvider } from "@/contexts/ScriptSettingsContext";
import { I18nProvider, useI18n } from "@/contexts/I18nContext";
import { FontSizeProvider, useFontSize } from "@/contexts/FontSizeContext";
import ProtectedRoute, { AdminRoute } from "@/components/ProtectedRoute";
import GatewayDemoSwitcher from "@/components/demo/GatewayDemoSwitcher";
import { tokenStore } from "@/lib/apiClient";
import InstallPrompt from "@/components/InstallPrompt";
import WhatsAppSupportButton from "@/components/WhatsAppSupportButton";

import Landing from "@/pages/Landing";
import ExternalLogin from "@/pages/ExternalLogin";
import SsoCallback from "@/pages/SsoCallback";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import Dashboard from "@/pages/Dashboard";
import Modules from "@/pages/Modules";
import ModuleScreen from "@/pages/ModuleScreen";
import Programs from "@/pages/Programs";
import ProgramDetail from "@/pages/ProgramDetail";
import Certificate from "@/pages/Certificate";
import Settings from "@/pages/Settings";
import Profile from "@/pages/Profile";
import Achievements from "@/pages/Achievements";
import Community from "@/pages/Community";
import HowToUse from "@/pages/HowToUse";
import ApplyTranslator from "@/pages/ApplyTranslator";
import School from "@/pages/School";

import AdminShell from "@/pages/admin/AdminShell";
import AdminUsers from "@/pages/admin/AdminUsers";
import AdminModules from "@/pages/admin/AdminModules";
import AdminRecitations from "@/pages/admin/AdminRecitations";
import AdminCertificates from "@/pages/admin/AdminCertificates";
import AdminCertificateTemplates from "@/pages/admin/AdminCertificateTemplates";
import AdminPrograms from "@/pages/admin/AdminPrograms";
import AdminAnalytics from "@/pages/admin/AdminAnalytics";
import AdminFeedback from "@/pages/admin/AdminFeedback";
import AdminBroadcast from "@/pages/admin/AdminBroadcast";
import AdminAuditLog from "@/pages/admin/AdminAuditLog";
import AdminOrganizations from "@/pages/admin/AdminOrganizations";
import AdminTranslations from "@/pages/admin/AdminTranslations";
import AdminTranslationEditor from "@/pages/admin/AdminTranslationEditor";
import AdminTranslatorApplications from "@/pages/admin/AdminTranslatorApplications";

// Translator accounts only ever land on Translations — every other /admin
// sub-page is off-limits to them, so the shell's default landing page can't
// stay hardcoded to "users" for every role.
function AdminIndexRedirect() {
  const { user } = useAuth();
  return <Navigate to={user?.role === "translator" ? "translations" : "users"} replace />;
}

// A signed-in visitor hitting "/" (typed URL, bookmark, reopening the
// browser) should land back on their Dashboard, not the signed-out pitch —
// mirrors ProtectedRoute's own onboarding gate so an unfinished onboarding
// still gets picked back up rather than skipped. Signed-out visitors (and
// the brief instant while the session is still being checked) keep seeing
// the normal Landing page.
function RootRoute() {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="min-h-[60vh] grid place-items-center" data-testid="auth-loading">
        <div className="w-10 h-10 rounded-full border-2 border-[#064E3B] border-t-transparent animate-spin" />
      </div>
    );
  }
  if (user) {
    // Straight into the app: onboarding is skipped (see ProtectedRoute).
    return <Navigate to="/dashboard" replace />;
  }
  return <Landing />;
}

function AppRouter() {
  return (
    <Routes>
      <Route path="/" element={<RootRoute />} />
      {/* Unconditional Landing, bypassing RootRoute's logged-in redirect —
          this is where the header logo points for every user (including
          signed-in ones) so "click the logo" always reaches the marketing
          page instead of bouncing straight back to /dashboard. */}
      <Route path="/landing" element={<Landing />} />
      <Route path="/login" element={<ExternalLogin />} />
      <Route path="/sso/callback" element={<SsoCallback />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />

      {/* Onboarding questions were removed; old links go straight to the dashboard. */}
      <Route path="/onboarding" element={<Navigate to="/dashboard" replace />} />
      <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
      <Route path="/modules" element={<ProtectedRoute><Modules /></ProtectedRoute>} />
      <Route path="/modules/:code" element={<ProtectedRoute><ModuleScreen /></ProtectedRoute>} />
      <Route path="/programs" element={<ProtectedRoute><Programs /></ProtectedRoute>} />
      <Route path="/programs/:id" element={<ProtectedRoute><ProgramDetail /></ProtectedRoute>} />
      <Route path="/certificate" element={<ProtectedRoute><Certificate /></ProtectedRoute>} />
      {/* The interactive review-others queue now lives on Community's Peer
          Review tab; per-module peer-assessment status stays on Certificate. */}
      <Route path="/peer-review" element={<Navigate to="/community?tab=peer" replace />} />
      <Route path="/achievements" element={<ProtectedRoute><Achievements /></ProtectedRoute>} />
      <Route path="/community" element={<ProtectedRoute><Community /></ProtectedRoute>} />
      <Route path="/school" element={<ProtectedRoute><School /></ProtectedRoute>} />
      {/* Public — no ProtectedRoute. The page is static content (three
          video walkthroughs, no user-specific data or API calls), and a
          signed-out visitor should be able to see how the app works before
          creating an account, same as the header's nav-how-to-use link now
          being visible to them too (see Header.jsx). */}
      <Route path="/how-to-use" element={<HowToUse />} />
      {/* Requires auth (unlike How To Use above) — submitting an application
          is tied to a real user_id server-side, never anonymous. */}
      <Route path="/apply-translator" element={<ProtectedRoute><ApplyTranslator /></ProtectedRoute>} />
      <Route path="/settings" element={<ProtectedRoute><Settings /></ProtectedRoute>} />
      <Route path="/profile" element={<ProtectedRoute><Profile /></ProtectedRoute>} />

      {/* The parent gate only checks "is this caller some kind of staff
          member" (default allow=admin/support/teacher). Each child route
          below adds its own stricter `allow` — without this, a teacher who
          types e.g. /admin/analytics directly gets the page shell client-side
          and only fails on the underlying API calls, instead of a clean
          redirect. Sidebar visibility (AdminShell's NAV roles) was already
          correct; this closes the direct-navigation gap. */}
      <Route path="/admin" element={<AdminRoute allow={["admin", "support", "mentor", "translator"]}><AdminShell /></AdminRoute>}>
        <Route index element={<AdminIndexRedirect />} />
        {/* Widening the parent gate above to admit Translator means every
            child route below without its own `allow` would otherwise
            silently inherit that wider set — users/recitations need an
            explicit narrower `allow` now to stay Translator-off-limits. */}
        <Route path="users" element={<AdminRoute allow={["admin", "support", "mentor"]}><AdminUsers /></AdminRoute>} />
        <Route path="modules" element={<AdminRoute allow={["admin", "support", "translator"]}><AdminModules /></AdminRoute>} />
        <Route path="recitations" element={<AdminRoute allow={["admin", "support", "mentor"]}><AdminRecitations /></AdminRoute>} />
        <Route path="certificates" element={<AdminRoute allow={["admin", "support"]}><AdminCertificates /></AdminRoute>} />
        <Route path="cert-templates" element={<AdminRoute allow={["admin", "support"]}><AdminCertificateTemplates /></AdminRoute>} />
        <Route path="programs" element={<AdminRoute allow={["admin", "support"]}><AdminPrograms /></AdminRoute>} />
        <Route path="feedback" element={<AdminRoute allow={["admin", "support"]}><AdminFeedback /></AdminRoute>} />
        <Route path="broadcast" element={<AdminRoute allow={["admin", "support"]}><AdminBroadcast /></AdminRoute>} />
        <Route path="organizations" element={<AdminRoute allow={["admin", "support"]}><AdminOrganizations /></AdminRoute>} />
        <Route path="analytics" element={<AdminRoute allow={["admin"]}><AdminAnalytics /></AdminRoute>} />
        <Route path="audit-log" element={<AdminRoute allow={["admin"]}><AdminAuditLog /></AdminRoute>} />
        <Route path="translations" element={<AdminRoute allow={["admin", "translator"]}><AdminTranslations /></AdminRoute>} />
        <Route path="translations/:code" element={<AdminRoute allow={["admin", "translator"]}><AdminTranslationEditor /></AdminRoute>} />
        <Route path="translator-applications" element={<AdminRoute allow={["admin", "support"]}><AdminTranslatorApplications /></AdminRoute>} />
      </Route>

      <Route path="*" element={<Landing />} />
    </Routes>
  );
}

// Writes the actual root font-size, combining two independent inputs that
// live in separate contexts: the user's own A-/A+ accessibility step
// (FontSizeContext, 90/100/112/125%) and a fixed boost applied only when
// the UI is Urdu or Tamil — both self-hosted display fonts (Alvi
// Nastaleeq, TAU_Elango_Kalyani) whose letterforms read noticeably
// smaller/thinner than the Latin-sans/Noto fallback they replaced at the
// same declared font-size. Tamil's boost is smaller than Urdu's: Kalyani's
// base glyphs are thin but upright (no RTL joining fighting legibility on
// top of the thinness the way Nastaleeq's cursive script does). Has to
// live here, inside both providers, rather than inside FontSizeProvider
// itself — FontSizeProvider sits *above* I18nProvider in the tree and so
// can't call useI18n(). Rendered for its effect only.
const URDU_SIZE_BOOST = 1.3;
const TAMIL_SIZE_BOOST = 1.15;
function FontSizeDomSync() {
  const { sizePercent } = useFontSize();
  const { lang } = useI18n();
  useEffect(() => {
    const boost = lang === "ur" ? URDU_SIZE_BOOST : lang === "ta" ? TAMIL_SIZE_BOOST : 1;
    document.documentElement.style.fontSize = `${Math.round(sizePercent * boost)}%`;
  }, [sizePercent, lang]);
  return null;
}

export default function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <FontSizeProvider>
          <I18nProvider>
            <FontSizeDomSync />
            <ScriptSettingsProvider>
              <OfflineProvider>
                <Toaster position="top-center" richColors />
                <InstallPrompt />
                <WhatsAppSupportButton />
                <AppRouter />
                <GatewayDemoSwitcher gatewayUrl={process.env.REACT_APP_GATEWAY_URL} getToken={tokenStore.get} setToken={tokenStore.set}
                  homes={{ default: "/dashboard", principal: "/school", teacher: "/school" }} />
              </OfflineProvider>
            </ScriptSettingsProvider>
          </I18nProvider>
          </FontSizeProvider>
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}
